import { IOptionsWithTypes, spLogTags } from '@eventr/common'
import { ColoursObj } from '@eventr/common-storybook'
import { Stack } from '@mui/material'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'
import i18n from '../../../i18n/i18next'
import { bs, useBoundStore } from '../../../state/bound.store'
import { defaultAddSnack } from '../../../state/overlayState/overlayContext'
import { loadingSignal } from '../../../state/overlayState/overlaySignals'
import { safeSignalSet } from '../../../utils'
import { EditMembersProvider } from '../../editItinerary/editMembers/editMembersProvider'
import { MembersList } from '../../editItinerary/editMembers/membersList'
import { NavigationView } from '../../layout/NavigationView'
import { useChatBodyContext } from '../chatBody/chatProvider'
import {
    addUsersToChannel,
    removeUsersFromGroupChannel,
    useRocketChatContext,
} from '../rocketChatProvider'

export const ManageGroupChannel = () => {
    const { orgConfig, wpUser, users } = useBoundStore(
        useShallow((s) => ({
            orgConfig: s.orgConfig,
            wpUser: s.wpUser,
            users: s.users,
        }))
    )

    const { chatView } = useRocketChatContext()
    const { members } = useChatBodyContext()

    const allUsersInChannel: IOptionsWithTypes[] = (members?.value ?? [])
        .map((user) => {
            const wpId = user?.customFields?.wpUserID
            if (wpId) {
                return { type: 'User', option: wpId } as IOptionsWithTypes
            }
            return undefined
        })
        .filter((s) => s !== undefined)

    const currentUrlSplit = useLocation()?.pathname?.split('/') ?? []
    const roomID = currentUrlSplit[currentUrlSplit.length - 1]

    const [selectedUsers, setSelectedUsers] =
        useState<IOptionsWithTypes[]>(allUsersInChannel)

    const onSave = async () => {
        const originalIds = new Set(
            allUsersInChannel.map((user) => user.option)
        )
        const selectedIds = new Set(selectedUsers.map((user) => user.option))

        const removedUsers = allUsersInChannel.filter(
            (user) => !selectedIds.has(user.option)
        )

        const addedUsers = selectedUsers.filter(
            (user) => !originalIds.has(user.option)
        )
        try {
            loadingSignal.value = true

            let failed = false // Flag to track errors

            if (removedUsers.length > 0) {
                await removeUsersFromGroupChannel({
                    channel_id: roomID,
                    user_ids: removedUsers.map((s) => s.option.toString()),
                }).then(async (responses) => {
                    for (const response of responses) {
                        if (response.status === 'error') {
                            const error = removeErrorMessage(
                                response.error_type
                            )
                            defaultAddSnack(error, 'error')
                            spLogTags('Error removing user from channel', [
                                'rocketchat',
                                'ERROR',
                            ])
                            spLogTags(response, ['rocketchat', 'ERROR'])

                            failed = true
                            throw new Error('User removal failed')
                        }
                    }
                })
            }

            if (failed) return

            if (addedUsers.length > 0) {
                await addUsersToChannel({
                    channel_id: roomID,
                    user_ids: addedUsers.map((s) => s.option.toString()),
                }).then(async (responses) => {
                    for (const response of responses) {
                        if (response.status === 'error') {
                            const error = addErrorMessage(response.error_type)
                            defaultAddSnack(error, 'error')
                            spLogTags('Error adding user to channel', [
                                'rocketchat',
                                'ERROR',
                            ])
                            spLogTags(response, ['rocketchat', 'ERROR'])

                            failed = true
                            throw new Error('User addition failed')
                        }
                    }
                })
            }

            if (!failed) {
                safeSignalSet(chatView, 'default')
                window.pushPath('tabs/rocketChat')
            }
        } catch (error) {
            defaultAddSnack('Error managing channel', 'error')
            spLogTags('Exception managing channel', ['rocketchat', 'ERROR'])
            spLogTags(error, ['rocketchat', 'ERROR'])
        } finally {
            loadingSignal.value = false
        }
    }

    return (
        <EditMembersProvider>
            <Stack
                height="100dvh"
                sx={{ backgroundColor: ColoursObj.backgrounds.primary }}
            >
                <NavigationView
                    navigationBarTitle="Manage Group Chat"
                    backButtonAction={() => {
                        safeSignalSet(chatView, 'chatInfo')
                    }}
                    navigationBarTrailing={[
                        {
                            id: 'createButton',
                            buttonText: 'Save',
                            disabled: selectedUsers.length >= 2 ? false : true,
                            colour: ColoursObj.core.primary,
                            useButton: true,
                            action: async () => onSave(),
                        },
                    ]}
                    usePageTransition
                    userSearchBar
                    removeTopPadding
                >
                    <Stack
                        padding="10px 21px 50px"
                        display="flex"
                        flexDirection="column"
                        height="100%"
                    >
                        <MembersList
                            users={users}
                            selectedUsers={selectedUsers ?? []}
                            setSelectedUsers={setSelectedUsers}
                            allowSelfRemove
                            searchBar={false}
                            onRefresh={async () => {
                                await Promise.all([
                                    bs().getProfile(),
                                    bs().fetchUsers(),
                                    bs().forceItineraryMapUpdate(),
                                ])
                            }}
                        />
                    </Stack>
                </NavigationView>
            </Stack>
        </EditMembersProvider>
    )
}

function addErrorMessage(msg: string) {
    switch (msg) {
        case 'user_not_found':
            return i18n.t('snack.rocket.userNotFoundChat')
        case 'error-room-not-found':
            return i18n.t('snack.rocket.roomNotFound')
        case 'error-user-not-found':
            return i18n.t('snack.rocket.userNotFoundExists')
        case 'error-already-in-room':
            return i18n.t('snack.rocket.alreadyInRoom')
        case 'error-not-allowed':
            return i18n.t('snack.rocket.unauthorisedAdd')
        case 'error-invalid-user':
            return i18n.t('snack.rocket.invalidUserId')
        default:
            return i18n.t('snack.rocket.unknownError')
    }
}

function removeErrorMessage(msg: string) {
    switch (msg) {
        case 'user_not_found':
            return i18n.t('snack.rocket.userNotFoundChat')
        case 'error-room-not-found':
            return i18n.t('snack.rocket.roomNotFound')
        case 'channel_not_found':
            return i18n.t('snack.rocket.channelNotFound')
        case 'not_in_channel':
            return i18n.t('snack.rocket.userNotFoundExists')
        case 'missing_permissions':
            return i18n.t('snack.rocket.alreadyInRoom')
        case 'invalid_room_type':
            return i18n.t('snack.rocket.unauthorisedAdd')
        case 'error-invalid-user':
            return i18n.t('snack.rocket.invalidUserId')
        default:
            return i18n.t('snack.rocket.unknownError')
    }
}
